import { useUiTranslator } from "../hooks/useUiTranslator";
import { useAtomValue } from "jotai";

import { useEffect, useRef } from "react";

import { toast } from "@4pace-cpq/core";

import { globalErrorEventAtom } from "./globalErrorEvent";

/**
 * Shows a snackbar for backend errors that carry an app-specific key/message
 * (see `classifyQueryError`) — a generalization of the per-button
 * `showErrorToast(t(\`CustomApp:${key}\`, ...))` pattern used throughout the
 * app (BtnAskForQuote, BtnImportConfigId, BtnJumpToEasy, BtnNeedHelp).
 *
 * Renders nothing — mount once, inside `Providers.tsx`, below
 * `UiTranslationsProvider` so `useUiTranslator()` is available.
 */
export function GlobalErrorNotifier(): null {
  const t = useUiTranslator();
  const event = useAtomValue(globalErrorEventAtom);
  const lastHandledId = useRef<number | null>(null);

  useEffect(() => {
    if (!event || event.id === lastHandledId.current) return;
    lastHandledId.current = event.id;

    const message = event.key
      ? t(
          event.key,
          event.fallbackMessage ??
            "An error occurred while processing your request.",
          { params: event.params },
        )
      : (event.fallbackMessage ??
        t(
          "CustomApp:toast.error.generic",
          "An error occurred while processing your request.",
        ));

    toast.error(message, { autoClose: 8000 });
  }, [event, t]);

  return null;
}
